'use client';
import type { CreateVaultExistsModalProps } from '@/presentation/components/modals/CreateVaultExistsModal';
import type { CreateVaultInvalidInterfaceModalProps } from '@/presentation/components/modals/CreateVaultInvalidInterfaceModal';
import type { CreateLiquidityPositionContainerProps } from '@/presentation/components/views/CreateLiquidityPosition/CreateLiquidityPositionContainer';
import type { CreatePositionContainerProps } from '@/presentation/components/views/CreatePosition/CreatePositionContainer';
import type { ManageInventoryPositionContainerProps } from '@/presentation/components/views/ManageInventoryPosition/ManageInventoryPositionContainer';
import type { ManageLiquidityPositionContainerProps } from '@/presentation/components/views/ManageLiquidityPosition/ManageLiquidityPositionContainer';
import type { SwapContainerProps } from '@/presentation/components/views/Swap/SwapContainer';
import type { SwapSearchContainerProps } from '@/presentation/components/widgets/Swap/SwapSearch';
import type { AssetContainerProps } from '@/presentation/components/views/Asset/AssetContainer';
import { Address } from 'nftx.js';
import { createContext, Dispatch, ReactNode, useContext } from 'react';
import type { IncreaseLiquidityProps } from '../../components/views/IncreaseLiquidity';
import { MigrateModalProps } from '@/presentation/components/modals/MigrateModal';
import { MigrateVTokenModalProps } from '@/presentation/components/modals/MigrateVTokenModal';
import { MigrateXTokenModalProps } from '@/presentation/components/modals/MigrateXTokenModal';
import { MigrateSlpModalProps } from '@/presentation/components/modals/MigrateSlpModal';
import { MigrateXSlpModalProps } from '@/presentation/components/modals/MigrateXSlpModal';

export interface SwapSearchModalProps extends SwapSearchContainerProps {}
export interface SwapModalProps extends SwapContainerProps {}
export interface ManageLiquidityPositionModalProps
  extends ManageLiquidityPositionContainerProps {}

export interface ManageInventoryPositionModalProps
  extends ManageInventoryPositionContainerProps {}

export interface AssetDetailModalProps extends AssetContainerProps {}

export interface CreatePositionModalProps
  extends CreatePositionContainerProps {}

export interface CreateInventoryPositionProps {
  vaultId: string;
  using?: 'nft' | 'vtoken';
}

export interface CreateLiquidityPositionProps
  extends CreateLiquidityPositionContainerProps {}

export interface CreateVaultOpenseaModalProps {
  onCancel: () => void;
}

export type { CreateVaultExistsModalProps };

export interface CreateVaultInvalidInterfaceProps
  extends CreateVaultInvalidInterfaceModalProps {}

export interface ClaimRewardsProps {
  vaultId?: string;
  viewMode?: 'liquidity' | 'inventory' | 'all';
}

export interface WithdrawLiquidityProps {
  positionId: Address;
}

export type WithdrawInventoryProps = { positionId: string };

export type { IncreaseLiquidityProps };

export interface ModalContextInterface {
  modal: ReactNode;
  setModal: Dispatch<any>;
  closeModal: (key?: string) => void;
  showSwapModal: (props: SwapModalProps) => void;
  showSwapSearchModal: (props: SwapSearchModalProps) => void;
  showCreateVaultExistsModal: (props: CreateVaultExistsModalProps) => void;
  showCreateVaultOpenseaModal: (props: CreateVaultOpenseaModalProps) => void;
  showCreateVaultInvalidInterfaceModal: (
    props: CreateVaultInvalidInterfaceProps
  ) => void;
  showCreatePositionModal: (props: CreatePositionModalProps) => void;
  showCreateInventoryPositionModal: (
    props: CreateInventoryPositionProps
  ) => void;
  showCreateLiquidityPositionModal: (
    props: CreateLiquidityPositionProps
  ) => void;
  showManageLiquidityPositionModal: (
    props: ManageLiquidityPositionModalProps
  ) => void;
  showManageInventoryPositionModal: (
    props: ManageInventoryPositionModalProps
  ) => void;
  showClaimRewardsModal: (props: ClaimRewardsProps) => void;
  showWithdrawLiquidityModal: (props: WithdrawLiquidityProps) => void;
  showWithdrawInventoryModal: (props: WithdrawInventoryProps) => void;
  showIncreaseLiquidityModal: (props: IncreaseLiquidityProps) => void;
  showAssetDetailModal: (props: AssetDetailModalProps) => void;
  showFaucetModal: () => void;
  showMigrateModal: (props: MigrateModalProps) => void;
  showMigrateVTokenModal: (props: MigrateVTokenModalProps) => void;
  showMigrateXTokenModal: (props: MigrateXTokenModalProps) => void;
  showMigrateSlpModal: (props: MigrateSlpModalProps) => void;
  showMigrateXSlpModal: (props: MigrateXSlpModalProps) => void;
}

export const ModalContext = createContext<ModalContextInterface>(null as any);

export const useModalContext = () => {
  return useContext(ModalContext);
};
